import { useState } from "react";
import { ShieldAlert, Undo2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bore360Panel, B360_LEVELS } from "@/components/Bore360Panel";

export const Bore360LabCard = ({ testId, assessment, status, onChanged }) => {
  const [showModal, setShowModal] = useState(false);
  const [level, setLevel] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  if (!assessment) return null;

  const locked = status === "published";
  const override = assessment.override;

  const submit = async (targetLevel, targetReason) => {
    setSaving(true);
    try {
      await api.post(`/lab/tests/${testId}/bore360-override`, { level: targetLevel, reason: targetReason });
      toast.success(targetLevel ? `Level overridden to ${targetLevel}` : "Override cleared — auto level restored");
      setShowModal(false);
      setLevel(null);
      setReason("");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Override failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Bore360Panel assessment={assessment} dataTestId="lab-bore360-panel">
      {override && (
        <div data-testid="lab-bore360-override-chip" className="mt-4 rounded-xl bg-slate-900 text-white p-3.5 text-[13px] leading-relaxed">
          <span className="font-bold">Manual override active</span> — auto-derived level was{" "}
          <span className="font-bold">{assessment.auto_level}</span>, set to{" "}
          <span className="font-bold">{assessment.level}</span> by {override.by || "lab staff"}.
          {override.reason && <span className="block mt-1 italic text-slate-300">&ldquo;{override.reason}&rdquo;</span>}
        </div>
      )}

      {!locked ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            data-testid="lab-bore360-override-btn"
            variant="outline"
            onClick={() => { setLevel(override ? assessment.level : assessment.level); setShowModal(true); }}
            className="rounded-full font-semibold bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
          >
            <ShieldAlert className="w-4 h-4 mr-1.5" /> Override level
          </Button>
          {override && (
            <Button
              data-testid="lab-bore360-clear-override-btn"
              variant="outline"
              disabled={saving}
              onClick={() => submit(null, "Override cleared — reverting to auto-derived level")}
              className="rounded-full font-semibold bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
            >
              <Undo2 className="w-4 h-4 mr-1.5" /> Clear override
            </Button>
          )}
        </div>
      ) : (
        <p className="mt-4 text-xs text-slate-500 italic">Report published — the Bore 360 level is locked for this report.</p>
      )}

      {showModal && (
        <div data-testid="lab-bore360-override-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6">
            <h3 className="font-display text-lg font-semibold text-slate-900">Override Bore 360 level</h3>
            <p className="text-sm text-slate-600 mt-1.5">
              Auto-derived level: <strong>Level {assessment.auto_level ?? assessment.level}</strong>. The override and reason are recorded in the audit log.
            </p>
            <div className="mt-4 space-y-2">
              {[4, 3, 2, 1].map((n) => {
                const cfg = B360_LEVELS[n];
                const active = level === n;
                return (
                  <button
                    key={n}
                    type="button"
                    data-testid={`lab-bore360-level-option-${n}`}
                    onClick={() => setLevel(n)}
                    className={`w-full text-left rounded-xl border-2 px-4 py-2.5 text-sm font-semibold transition-colors ${active ? "text-white" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                    style={active ? { background: cfg.accent, borderColor: cfg.accent } : undefined}
                  >
                    Level {n} — {cfg.short}
                  </button>
                );
              })}
            </div>
            <div className="mt-4">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Reason for override (required, logged)</Label>
              <Textarea
                data-testid="lab-bore360-override-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Confirmatory retest received from reference lab — result within guideline"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button data-testid="lab-bore360-override-cancel" variant="ghost" onClick={() => setShowModal(false)} className="rounded-full">Cancel</Button>
              <Button
                data-testid="lab-bore360-override-save"
                disabled={saving || !level || reason.trim().length < 5}
                onClick={() => submit(level, reason.trim())}
                className="rounded-full font-semibold bg-slate-900 hover:bg-slate-800"
              >
                {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving…</> : "Apply override"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Bore360Panel>
  );
};
